import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { productPath, searchProducts } from "../data/productsData";
import { breadcrumbSchema, pageMeta } from "../data/seoData";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

const SearchResults = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get("q") || "";
  const [draft, setDraft] = useState(query);

  useEffect(() => setDraft(query), [query]);

  const results = useMemo(() => (query ? searchProducts(query) : []), [query]);

  /* Detail lives at one canonical URL, so a result opens the product page
     rather than a second drawer on the results URL. */
  const openProduct = useCallback(
    (product) => navigate(productPath(product)),
    [navigate]
  );

  const submit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (draft.trim()) next.set("q", draft.trim());
    setParams(next);
  };

  return (
    <>
      <Seo
        {...pageMeta.search}
        title={query ? `Search: ${query} | Devanshi Corporation` : pageMeta.search.title}
        schemas={[["breadcrumb", breadcrumbSchema([{ name: "Search", path: "/search" }])]]}
      />
      <header className="phero">
        <div className="phero__glow" />
        <div className="phero__grid" />
        <div className="shell phero__inner">
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>Search</span>
          </nav>

          <Reveal as="h1" className="phero__title" variant="mask">
            {query ? `“${query}”` : "Search the catalogue"}
          </Reveal>
          <Reveal as="p" className="phero__lead" delay={0.1}>
            {query
              ? `${results.length} product${results.length === 1 ? "" : "s"} match your search across names, formulas, applications and keywords.`
              : "Search across compound names, chemical formulas, grades and typical applications."}
          </Reveal>

          <Reveal delay={0.16}>
            <form className="sres__form" onSubmit={submit} role="search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. potassium, molybdate, fertilizer…"
                aria-label="Search products"
              />
              <button type="submit" className="btn btn--accent btn--sm">
                Search
              </button>
            </form>
          </Reveal>
        </div>
      </header>

      <section className="section on-light">
        <div className="shell">
          {results.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">
                <i className="fas fa-flask" aria-hidden="true" />
              </div>
              <h3>{query ? "No products matched" : "Start with a keyword"}</h3>
              <p>
                {query
                  ? `Nothing in stock matches “${query}”. We also import to specification, so send us the compound and grade you need.`
                  : "Try a compound name, a chemical formula, or the industry you are buying for."}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "0.6rem",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  marginTop: "1.5rem",
                }}>
                <Link to="/products" className="btn btn--ghost">
                  Browse all products
                </Link>
                <Link to="/contact" className="btn btn--accent">
                  Request a compound
                  <span className="btn__arrow" aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          ) : (
            <div className="product-grid">
              {results.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onOpen={openProduct}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default SearchResults;
